import React, { useEffect, useState } from 'react';
import { PaddleIcon, AlertTriangleIcon } from '../icons';
import { useRecording } from '../../hooks/useRecording';
import PlayerForm from './PlayerForm';
import RecordingScreen from './RecordingScreen';
import StartingScreen from './StartingScreen';
import BackgroundJobsBar from './BackgroundJobsBar';
import RecordingDetailModal from './RecordingDetailModal';
import { courtsApi } from '../../api';

const STORAGE_KEY = 'pb_selected_court';

export default function KioskApp() {
  const [courts, setCourts]       = useState([]);
  const [courtId, setCourtId]     = useState(null);
  const [courtName, setCourtName] = useState('');
  const [loadErr, setLoadErr]     = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);

  // Load all courts on mount
  useEffect(() => {
    courtsApi.list()
      .then(data => {
        const list = data.results || data;
        setCourts(list);

        // Restore last selected court from localStorage, default to Court 1
        const savedId = localStorage.getItem(STORAGE_KEY);
        const saved   = savedId ? list.find(c => String(c.id) === savedId) : null;
        const court   = saved || list.find(c => c.number === 1) || list[0];

        if (court) {
          setCourtId(court.id);
          setCourtName(court.name || `Court ${court.number}`);
        } else {
          setLoadErr('No courts found. Run: python manage.py seed_courts');
        }
      })
      .catch(() => setLoadErr('Cannot reach server. Is Django running on port 8000?'));
  }, []);

  const handleSelectCourt = (court) => {
    setCourtId(court.id);
    setCourtName(court.name || `Court ${court.number}`);
    localStorage.setItem(STORAGE_KEY, String(court.id));
    setShowPicker(false);
    reset();
  };

  const {
    phase, session, elapsedDisplay, error: recError,
    startRecording, stopRecording, reset,
    backgroundJobs, dismissJob,
  } = useRecording(courtId);

  if (loadErr) return <ErrorScreen message={loadErr} />;

  if (!courtId) return (
    <div style={s.splash}>
      <div style={s.splashIcon}><PaddleIcon size={44} /></div>
      <p style={s.splashText}>Connecting…</p>
    </div>
  );

  // Recordings that are still uploading/encoding/emailing in the background
  // render as a small non-blocking status stack on top of whichever screen
  // is showing below — starting a new recording is never gated on these.
  const jobsBar = (
    <>
      <BackgroundJobsBar jobs={backgroundJobs} onDismiss={dismissJob} onView={setViewingJob} />
      {viewingJob?.session && (
        <RecordingDetailModal
          session={viewingJob.session}
          courtName={courtName}
          onClose={() => setViewingJob(null)}
        />
      )}
    </>
  );

  if (phase === 'starting') {
    return (
      <>
        <StartingScreen
          courtName={courtName}
          playerEmail={session?.player_email || ''}
        />
        {jobsBar}
      </>
    );
  }

  if (phase === 'recording') {
    return (
      <>
        <RecordingScreen
          courtId={courtId}
          courtName={courtName}
          playerEmail={session?.player_email || ''}
          elapsedDisplay={elapsedDisplay}
          onStop={stopRecording}
        />
        {jobsBar}
      </>
    );
  }

  if (phase === 'error') {
    return (
      <>
        <ErrorScreen message={recError} onRetry={reset} />
        {jobsBar}
      </>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <PlayerForm courtName={courtName} onStart={startRecording} />
      {jobsBar}

      {/* Court switcher button — subtle, bottom right */}
      <button
        onClick={() => setShowPicker(true)}
        style={s.switchBtn}
        title="Switch court"
      >
        ⇄ Switch Court
      </button>

      {/* Court picker modal */}
      {showPicker && (
        <div style={s.overlay} onClick={() => setShowPicker(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Select Court</h3>
              <button onClick={() => setShowPicker(false)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.courtList}>
              {courts.map(court => (
                <button
                  key={court.id}
                  onClick={() => handleSelectCourt(court)}
                  style={{
                    ...s.courtOption,
                    ...(court.id === courtId ? s.courtOptionActive : {}),
                  }}
                >
                  <div style={s.courtOptionLeft}>
                    <div style={s.courtOptionNum}>Court {court.number}</div>
                    <div style={s.courtOptionName}>{court.name || `Court ${court.number}`}</div>
                  </div>
                  <div style={{
                    ...s.courtStatus,
                    background: court.current_status === 'recording'
                      ? 'var(--red-light)' : 'var(--green-light)',
                    color: court.current_status === 'recording'
                      ? 'var(--red)' : 'var(--green)',
                  }}>
                    {court.current_status === 'recording' ? '● Recording' : '○ Available'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  return (
    <div style={s.errPage}>
      <div style={s.errCard}>
        <div style={s.errIcon}><AlertTriangleIcon size={32} color="var(--red)" /></div>
        <h2 style={s.errTitle}>Something went wrong</h2>
        <p style={s.errMsg}>{message}</p>
        {onRetry && <button onClick={onRetry} style={s.retryBtn}>Try Again</button>}
      </div>
    </div>
  );
}

const s = {
  splash: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--gray-50)', fontFamily: 'var(--font)',
  },
  splashIcon: { fontSize: 56, marginBottom: 16 },
  splashText: { color: 'var(--gray-500)', fontSize: 18 },

  // Switch court button
  switchBtn: {
    position: 'fixed', bottom: 20, right: 20,
    background: 'rgba(255,255,255,0.9)',
    border: '1px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 16px', fontSize: 13,
    fontWeight: 600, color: 'var(--gray-600)',
    cursor: 'pointer', zIndex: 100,
    boxShadow: 'var(--shadow-sm)',
    fontFamily: 'var(--font)',
  },

  // Modal overlay
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: 'var(--white)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%', maxWidth: 400,
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease',
  },
  modalHeader: {
    padding: '20px 24px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: '1px solid var(--gray-100)',
  },
  modalTitle: {
    fontSize: 17, fontWeight: 700, color: 'var(--gray-900)', margin: 0,
  },
  closeBtn: {
    background: 'var(--gray-100)', border: 'none',
    borderRadius: 'var(--radius-sm)', width: 30, height: 30,
    fontSize: 14, cursor: 'pointer', color: 'var(--gray-600)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font)',
  },
  courtList: { padding: '8px 12px 12px' },
  courtOption: {
    width: '100%', padding: '14px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    border: '1.5px solid transparent',
    borderRadius: 'var(--radius-sm)',
    background: 'none', cursor: 'pointer',
    marginBottom: 6, textAlign: 'left',
    transition: 'background 0.15s, border-color 0.15s',
    fontFamily: 'var(--font)',
  },
  courtOptionActive: {
    background: 'var(--blue-light)',
    borderColor: 'var(--blue)',
  },
  courtOptionLeft: {},
  courtOptionNum: {
    fontSize: 11, fontWeight: 600, color: 'var(--gray-400)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2,
  },
  courtOptionName: { fontSize: 15, fontWeight: 600, color: 'var(--gray-900)' },
  courtStatus: {
    fontSize: 12, fontWeight: 600,
    padding: '4px 10px', borderRadius: 50,
  },

  // Error screen
  errPage: {
    minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--gray-50)', padding: 24, fontFamily: 'var(--font)',
  },
  errCard: {
    background: 'white', borderRadius: 'var(--radius-lg)',
    padding: '48px 40px', textAlign: 'center',
    boxShadow: 'var(--shadow)', maxWidth: 400, width: '100%',
  },
  errIcon: { fontSize: 48, marginBottom: 16 },
  errTitle: { fontSize: 22, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 10 },
  errMsg: { fontSize: 15, color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 28 },
  retryBtn: {
    background: 'var(--blue)', color: 'white', border: 'none',
    borderRadius: 'var(--radius-sm)', padding: '12px 32px',
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
};
